import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faLock,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resetPassword } from "../../../services/resetPasswordService";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { useTheme } from "../../../context/ThemeContext";
import secureStorage from "../../../utils/secureStorage";

function ResetPassword() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validation
    if (password !== confirmPassword) {
      setError(t("new_password_and_confirm_password_do_not_match"));
      toast.error(t("new_password_and_confirm_password_do_not_match"));
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError(t("password_must_be_at_least_8_characters_long"));
      toast.error(t("password_must_be_at_least_8_characters_long"));
      setLoading(false);
      return;
    }

    try {
      // Get token from secureStorage
      const token = secureStorage.getItem("email_token");

      if (!token) {
        setError("Session expired. Please start the process again.");
        toast.error("Session expired. Please start the process again.");
        setLoading(false);
        return;
      }

      const response = await resetPassword(token, password, confirmPassword);

      if (response.success) {
        toast.success(response.message || "Password reset successfully");

        // Clear secure storage
        secureStorage.removeItem("email_token");
        secureStorage.removeItem("reset_email");

        // Redirect to login page
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(
          response.message || "Failed to reset password. Please try again."
        );
        toast.error(response.message || "Failed to reset password");
      }
    } catch (err) {
      setError("Failed to reset password. Please try again.");
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0C1C]  px-4 sm:px-6 md:px-10 items-center justify-center">
      <div className="flex flex-col md:flex-row w-full pt-10">
        <div className="flex flex-col w-full items-center justify-center pb-5">
          <div 
            className="loginWrapper loginWrapper text-white w-full sm:w-11/12 md:w-2/4 lg:w-2/5 xl:w-2/5 rounded-xl px-5 sm:px-10 py-6 shadow-lg"
            style={{ backgroundColor: isDarkMode ? "#0A0C1C" : "#6D29F6" }}
          >
            {/* Back Button */}
            <div className="flex items-center mb-4">
              <button
                onClick={handleBack}
                className="flex items-center text-white hover:text-gray-300 transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                <span className="text-sm">{t("back")}</span>
              </button>
            </div>

            <div className="flex flex-col items-center justify-center text-center mb-4">
              {/* <img src={Logo} alt='Logo' className='w-12 sm:w-14 mt-2' /> */}
              <div className="text-center  flex flex-col gap-2 justify-center items-center">
                <img src="/logo-bg.png" alt="VibeLinks Logo" className="h-16" />
                <h1 className="text-2xl font-bold">
                  {t("app_name", "VibeLinks")}
                </h1>
              </div>
              <h1 className="text-xl sm:text-2xl font-semibold mt-2 md:hidden text-white">
                {t("reset_password")}
              </h1>
            </div>
            <form onSubmit={handleSubmit} className="w-full">
              <div className="mb-4">
                <label className="text-xs mb-1 text-white">
                  {t("new_password")}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500">
                    <FontAwesomeIcon icon={faLock} />
                  </span>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    style={isDarkMode ? { backgroundColor: '#ffffff', color: '#000000' } : { backgroundColor: '#ffffff', color: '#000000' }}
                    placeholder={t("enter_new_password")}
                    required
                  />
                  <span
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-500 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs mb-1 text-white">
                  {t("confirm_password")}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-500">
                    <FontAwesomeIcon icon={faLock} />
                  </span>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    style={isDarkMode ? { backgroundColor: '#ffffff', color: '#000000' } : { backgroundColor: '#ffffff', color: '#000000' }}
                    placeholder={t("confirm_new_password")}
                    required
                  />
                  <span
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-500 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <FontAwesomeIcon
                      icon={showConfirmPassword ? faEyeSlash : faEye}
                    />
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-lg font-semibold hover:bg-opacity-90 transition duration-150 flex items-center justify-center mt-6"
                style={{
                  backgroundColor: isDarkMode ? '#6d29f6' : 'white',
                  color: isDarkMode ? 'white' : '#6d29f6'
                }}
              >
                {loading ? (
                  <>
                    <Loader loaderColor={isDarkMode ? "white" : "[#6d29f6]"} />
                    <span className="ml-2">{t("saving")}</span>
                  </>
                ) : (
                  t("reset_password")
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
